import { FC, PropsWithChildren, useState, useCallback, useRef, useLayoutEffect } from 'react'
import ReactDataSheet from 'react-datasheet'
import dayjs from 'dayjs'
import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs'
import { Button } from '@progress/kendo-react-buttons'

import {
  ErrorMessage,
  ErrorMessages,
  FiscalMonthDropDownList
} from '../../../components/parts'
import {
  ManualYamadumiNewItem,
  SimulationCalculationInfoResItem
} from '@gateway.Simulation'

import 'react-datasheet/lib/react-datasheet.css'
import { MessageProvider } from '../../../services/resources/MessageProvider'
import { SimulationBuildingSearchActions, useAppDispatch, useAppSelector } from '../../../stores'

// 現在のYYYYMM
const systemTime = () => {
  const month = new Date().getMonth() + 1
  const year = new Date().getFullYear()
  const date = month > 9 ? `${year}${month}` : `${year}0${month}`
  return date
}

// カレンダ部の年度を算出
const createYearColumns = (startYearStr: string) => {
  const startYear = parseInt(startYearStr, 10)
  const emptyArray = Array.from({ length: 6 })

  return emptyArray.map<number>((_value, index) => {
    return startYear + index
  })
}
const year = Number(systemTime().substring(4, systemTime().length)) < 4 ? -2 : -1
const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const SheetRenderer: FC<
PropsWithChildren<{
  className: string
  displayStartYear: string
}>
> = ({ className, children, displayStartYear }) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const location = useAppSelector((state) => state.simulationbuildingsearch.location)
  const dispatch = useAppDispatch()

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location })
  }, [location])

  return (
    <div className="scrollable-container" style={{ maxHeight: 753 }} ref={ref} onScroll={(event) => {
      dispatch(SimulationBuildingSearchActions.setLocation(Number(ref.current?.scrollLeft)))
    }}>
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>
                自動<br></br>配分
              </div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 30 }}>表示</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 66 }}>
              <div style={{ width: 110 }}>部位</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 179 }}>
              <div style={{ width: 70 }}>分割業者数</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 252 }}>
              <div style={{ width: 70 }}>分割</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 325 }}>
              <div style={{ width: 80 }}>手積反映</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 408 }}>
              <div style={{ width: 170 }}>開始年月</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 581 }}>
              <div style={{ width: 70 }}>工期月数</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {MONTHS.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

const RowRenderer: FC<
PropsWithChildren<{
  rowNo: number
  rowLength: number
  manualYamadumiList: ManualYamadumiNewItem[]
  isReadonlyDisabled: boolean
  onDisplayCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onAutoAllocationCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onSplitSupplierNumberChange: (
    selectedRowIndex: number,
    splitSupplierNumber: number
  ) => void
  handleSplit: (item: ManualYamadumiNewItem) => void
  onChangeMonth: (
    selectedRowIndex: number,
    year: string,
    month: string
  ) => void
  onChangeYear: (
    selectedRowIndex: number,
    year: string,
    month: string
  ) => void
  onScheduleMonthsNumberChange: (
    selectedRowIndex: number,
    scheduleMonthsNumber: number
  ) => void
  onLaborBtnClick: () => void
}>
> = ({
  rowNo,
  children,
  manualYamadumiList,
  onDisplayCheckboxChange,
  onAutoAllocationCheckboxChange,
  onSplitSupplierNumberChange,
  handleSplit,
  onChangeMonth,
  onChangeYear,
  onScheduleMonthsNumberChange,
  onLaborBtnClick,
  isReadonlyDisabled
}) => {
  /**
   * startM
   */
  const getStartMonth = (dateMonth: string) => {
    return dateMonth?.substring(4)
  }
  /**
   * startY
   */
  const getStartYear = (dateYear: string) => {
    return dateYear?.substring(0, 4)
  }
  if (manualYamadumiList[rowNo].display === 'block') {
    if (manualYamadumiList[rowNo].summaryFlg === 1) {
      return (
        <tr style={{ height: 33 }}>
          <th style={{ position: 'sticky', left: 0 }}>
            <Checkbox
              disabled={isReadonlyDisabled}
              checked={manualYamadumiList[rowNo]?.autoAllocationFlg === 1}
              onChange={(e) => onAutoAllocationCheckboxChange(rowNo, e.value)}
            />
          </th>
          <th style={{ position: 'sticky', left: 33 }}>
            <Checkbox
              checked={manualYamadumiList[rowNo]?.displayFlg === 1}
              onChange={(e) => onDisplayCheckboxChange(rowNo, e.value)}
            />
          </th>
          <th style={{ position: 'sticky', left: 66 }}>
            {manualYamadumiList[rowNo]?.partNm}
          </th>
          <th style={{ position: 'sticky', left: 179 }}>
            {manualYamadumiList[rowNo]?.partNm !== null &&
            <NumericTextBox
              width={70}
              size="small"
              format={'n0'}
              min={0}
              value={manualYamadumiList[rowNo]?.splitSupplierNumber}
              disabled={isReadonlyDisabled}
              onChange={(e) => onSplitSupplierNumberChange(rowNo, e.value ?? 0)}
            />
            }
          </th>
          <th style={{ position: 'sticky', left: 252 }}>
            {manualYamadumiList[rowNo]?.partNm !== null &&
             <Button
              disabled={isReadonlyDisabled}
              style={{
                width: '60px',
                height: '25px',
                backgroundColor: 'silver',
                color: 'white'
              }}
              onClick={() => handleSplit(manualYamadumiList[rowNo])}
            >
              分割
            </Button>}
          </th>
          <th style={{ position: 'sticky', left: 325 }}>
            <Button
              disabled={isReadonlyDisabled}
              style={{
                width: '75px',
                height: '25px',
                backgroundColor: 'silver',
                color: 'white'
              }}
              onClick={onLaborBtnClick}
            >
              手積反映
            </Button>
          </th>
          <th style={{ position: 'sticky', left: 408 }}>
            <FiscalMonthDropDownList
              disabled={isReadonlyDisabled}
              origin={year}
              range={6}
              fmddlValue={
                Number(getStartMonth(manualYamadumiList[rowNo].startYm ?? '')) === 0
                  ? undefined
                  : Number(getStartMonth(manualYamadumiList[rowNo].startYm ?? ''))
              }
              fyddlValue={
                Number(getStartYear(manualYamadumiList[rowNo].startYm ?? '')) === 0
                  ? undefined
                  : Number(getStartYear(manualYamadumiList[rowNo].startYm ?? ''))
              }
              onMonthChange={(month) =>
                onChangeMonth(
                  rowNo,
                  getStartYear(manualYamadumiList[rowNo].startYm ?? ''),
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  ('00' + month).slice(-2)
                )
              }
              onYearChange={(year) =>
                onChangeYear(
                  rowNo,
                  year.toString(),
                  getStartMonth(manualYamadumiList[rowNo].startYm ?? '')
                )
              }
              popupSettings={{ className: 'pcy-dropdownlist' }}
            />
          </th>
          <th style={{ position: 'sticky', left: 581 }}>
            <NumericTextBox
              width={70}
              size="small"
              format={'n0'}
              min={0}
              value={manualYamadumiList[rowNo].scheduleMonths ?? 0}
              disabled={isReadonlyDisabled}
              onChange={(e) =>
                onScheduleMonthsNumberChange(rowNo, e.value ?? 0)
              }
            />
          </th>
          {children}
        </tr>
      )
    }
    if (manualYamadumiList[rowNo].summaryFlg === 0) {
      return (
        <tr style={{ height: 33 }}>
          <th style={{ position: 'sticky', left: 0 }}>
            <Checkbox
              disabled
              checked={manualYamadumiList[rowNo]?.autoAllocationFlg === 1}
              onChange={(e) => onAutoAllocationCheckboxChange(rowNo, e.value)}
            />
          </th>
          <th style={{ position: 'sticky', left: 33 }}>
            <Checkbox
              checked={manualYamadumiList[rowNo]?.displayFlg === 1}
              onChange={(e) => onDisplayCheckboxChange(rowNo, e.value)}
            />
          </th>
          <th style={{ position: 'sticky', left: 66 }}>
            {manualYamadumiList[rowNo]?.partNm}
          </th>
          <th style={{ position: 'sticky', left: 179 }}></th>
          <th style={{ position: 'sticky', left: 252 }}></th>
          <th style={{ position: 'sticky', left: 325 }}></th>
          <th style={{ position: 'sticky', left: 408 }}>
            <FiscalMonthDropDownList
              disabled
              origin={0}
              range={6}
              fmddlValue={
                Number(getStartMonth(manualYamadumiList[rowNo].startYm ?? '')) === 0
                  ? undefined
                  : Number(getStartMonth(manualYamadumiList[rowNo].startYm ?? ''))
              }
              fyddlValue={
                Number(getStartYear(manualYamadumiList[rowNo].startYm ?? '')) === 0
                  ? undefined
                  : Number(getStartYear(manualYamadumiList[rowNo].startYm ?? ''))
              }
              onMonthChange={(month) =>
                onChangeMonth(
                  rowNo,
                  getStartYear(manualYamadumiList[rowNo].startYm ?? ''),
                  month.toString()
                )
              }
              onYearChange={(year) =>
                onChangeYear(
                  rowNo,
                  year.toString(),
                  getStartMonth(manualYamadumiList[rowNo].startYm ?? '')
                )
              }
            />
          </th>
          <th style={{ position: 'sticky', left: 581 }}>
            <NumericTextBox
              width={70}
              size="small"
              format={'n0'}
              min={0}
              disabled
              value={manualYamadumiList[rowNo].scheduleMonths ?? 0}
              onChange={(e) => onSplitSupplierNumberChange(rowNo, e.value ?? 0)}
            />
          </th>
          {children}
        </tr>
      )
    }
  }

  return null
}

// セルの設定
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  manualYamadumiList: ManualYamadumiNewItem[]
}> = ({ valueViewerProps, manualYamadumiList }) => {
  const backgroundStyle =
    valueViewerProps.cell.value != null && valueViewerProps.cell.value > 0
      ? manualYamadumiList[valueViewerProps.row].partGroup === '1' ||
        manualYamadumiList[valueViewerProps.row].partGroup === '5'
        ? { backgroundColor: '#99FF99', height: '100%' }
        : manualYamadumiList[valueViewerProps.row].partGroup === '2' ||
          manualYamadumiList[valueViewerProps.row].partGroup === '6'
          ? { backgroundColor: '#FCD5B4', height: '100%' }
          : manualYamadumiList[valueViewerProps.row].partGroup === '3' ||
          manualYamadumiList[valueViewerProps.row].partGroup === '7'
            ? { backgroundColor: '#FFFF00', height: '100%' }
            : manualYamadumiList[valueViewerProps.row].partGroup === '4' ||
          manualYamadumiList[valueViewerProps.row].partGroup === '8'
              ? { backgroundColor: '#BFBFBF', height: '100%' }
              : undefined
      : undefined

  return <div style={backgroundStyle}>{valueViewerProps.value}</div>
}

export type CellValue = number
export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}

interface ManualSimulationSheetProps {
  manualYamadumiList: ManualYamadumiNewItem[]
  workYamadumi: SimulationCalculationInfoResItem
  isReadonlyDisabled: boolean
  onDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onAutoAllocationCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onSplitSupplierNumberChange: (
    selectedRowIndex: number,
    splitSupplierNumber: number
  ) => void
  handleSplit: (item: ManualYamadumiNewItem) => void
  onChangeYear: (selectedRowIndex: number, year: string, month: string) => void
  onChangeMonth: (selectedRowIndex: number, year: string, month: string) => void
  onScheduleMonthsNumberChange: (
    selectedRowIndex: number,
    scheduleMonthsNumber: number
  ) => void
  resultDateList: (resultIndexList: Array<number | null>, selectedRowIndex: number) => void
  onCellsChangedManualSimulationSheet: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => void
  displayStartYear: string
}

export const ManualSimulationSheet: FC<ManualSimulationSheetProps> = (
  props
) => {
  const exactNumberDigits = (num: number) => {
    return Number(String(num).replace(/^(.*\..{4}).*$/, '$1'))
  }
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  /**
   * 全体（自動）
   */
  const getAllAutomatic = () => {
    if (props?.manualYamadumiList.length > 0) {
      return props?.manualYamadumiList[0]?.workQuantity?.map((_item, index) => {
        let sum = 0

        props.manualYamadumiList?.map((el) => {
          if (el.summaryFlg === 0 && el.display === 'block') {
            sum += Number(el.workQuantity?.[index]?.value)
          }
          return el
        })

        return {
          value: exactNumberDigits(sum),
          readOnly: true,
          summaryFlg: 0
        }
      })
    }
  }

  /**
   * 全体（手動）
   */
  const getAllManual = () => {
    return props?.manualYamadumiList[0]?.workQuantity?.map((_, index) => {
      let sum = 0

      props.manualYamadumiList?.map((el) => {
        if (el.summaryFlg === 1) {
          sum += Number(el.workQuantity?.[index]?.value)
        }
        return el
      })
      return {
        value: exactNumberDigits(sum),
        readOnly: true,
        summaryFlg: 0
      }
    })
  }

  /**
   * 合計行の計算を行う
   */
  const createData = (list: ManualYamadumiNewItem[]) => {
    const workQuantityList = list.map((item) => {
      return item.workQuantity
    })
    // @ts-expect-error:next-line
    workQuantityList[workQuantityList.length - 1] = getAllManual()
    // @ts-expect-error:next-line
    workQuantityList[workQuantityList.length - 2] = getAllAutomatic()
    return workQuantityList as GridElement[][]
  }
  /**
   * 手積反映ボタン押下
   */
  const handleReflectedNumberRequests = useCallback((rowRendererPropsRow: number) => {
    const isScheduleMismatchCheck = scheduleMismatchCheck(
      props.manualYamadumiList,
      rowRendererPropsRow,
      props.workYamadumi
    )
    if (!isScheduleMismatchCheck) {
      const workQuantityLength = props?.manualYamadumiList[0]?.workQuantity?.length ?? 0
      const startYm = props.manualYamadumiList[rowRendererPropsRow].startYm ?? null
      const currentScheduleMonths = props.manualYamadumiList[rowRendererPropsRow].scheduleMonths ?? 0
      const startYmDate = new Date(Number(startYm?.slice(0, 4)), (Number(startYm?.slice(4)) - 1))
      startYmDate.setMonth((startYmDate.getMonth() + currentScheduleMonths) - 1)
      const initDateList: Array<number | null> = []
      const lastNendoStart = new Date(dayjs().add(-1, 'year').add(-3, 'month').year(), 3)
      Array.from({ length: workQuantityLength }).map((item) => {
        const year = lastNendoStart.getFullYear()
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const month = ('00' + (lastNendoStart.getMonth() + 1)).slice(-2)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        initDateList.push(Number(year + month))
        lastNendoStart.setMonth(lastNendoStart.getMonth() + 1)
        return item
      })
      initDateList.map((item, index) => {
        if (item !== null) {
          if (item < Number(startYm)) {
            initDateList[index] = null
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          } else if (item >= Number(startYm) && item <= Number(startYmDate.getFullYear() + ('00' + (startYmDate.getMonth() + 1)).slice(-2))) {
            initDateList[index] = index
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          } else if (item > Number(startYmDate.getFullYear() + ('00' + (startYmDate.getMonth() + 1)).slice(-2))) {
            initDateList[index] = null
          }
        }
        return item
      })
      props.resultDateList(initDateList, rowRendererPropsRow)
    } else {
      // エラーダイアログを表示する
      setErrorMessages([{
        id: 'E025',
        content: MessageProvider.provide(
          'E025',
          ...[]
        )
      }])
    }
  }, [props.manualYamadumiList, props.workYamadumi])

  /** Table Header */
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer className={sheetRendererProps.className} displayStartYear={props.displayStartYear}>
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }
  /**
   *工期不整合チェック
   */
  const scheduleMismatchCheck = (
    manualYamadumiList: ManualYamadumiNewItem[],
    rowRendererPropsRow: number,
    workYamadumi: SimulationCalculationInfoResItem
  ) => {
    let flg = false
    if (workYamadumi.autoCalculationMethod === 'A') {
      let partSchedule = 0
      manualYamadumiList.map(item => {
        if (item.partGroup === manualYamadumiList[rowRendererPropsRow].partGroup && item.summaryFlg === 1) {
          partSchedule += item.scheduleMonths ?? 0
        }
        return partSchedule
      })
      if ((manualYamadumiList[rowRendererPropsRow].partGroup === '1' && workYamadumi.scheduleFoundation !== partSchedule) ||
         (manualYamadumiList[rowRendererPropsRow].partGroup === '2' && (workYamadumi.scheduleUnderground ?? 0) + (workYamadumi.scheduleFloorPrecedence ?? 0) !== partSchedule) ||
         (manualYamadumiList[rowRendererPropsRow].partGroup === '3' && workYamadumi.scheduleGround !== partSchedule)) {
        flg = true
      }
    }
    return flg
  }
  /** Table Row[Lift + Right(→rowRendererProps.children)] */
  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean, splitSupplierNumber: number }
  ) => {
    if (props.manualYamadumiList.length <= 2) return
    return (
      <RowRenderer
        rowNo={rowRendererProps.row}
        rowLength={props.manualYamadumiList?.length}
        manualYamadumiList={props.manualYamadumiList}
        onDisplayCheckboxChange={props.onDisplayCheckboxChange}
        onAutoAllocationCheckboxChange={props.onAutoAllocationCheckboxChange}
        onSplitSupplierNumberChange={props.onSplitSupplierNumberChange}
        handleSplit={props.handleSplit}
        onChangeMonth={props.onChangeMonth}
        onChangeYear={props.onChangeYear}
        onScheduleMonthsNumberChange={props.onScheduleMonthsNumberChange}
        isReadonlyDisabled={props.isReadonlyDisabled}
        onLaborBtnClick={() => { handleReflectedNumberRequests(rowRendererProps.row) }
        }
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={props.manualYamadumiList.length}
        manualYamadumiList={props.manualYamadumiList}
      />
    )
  }

  return (
    <div>
      <ReactDataSheet<GridElement, CellValue>
        data={createData(props.manualYamadumiList ?? [])}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        onCellsChanged={props.onCellsChangedManualSimulationSheet}
      />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </div>
  )
}
